import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { bool, date, number, object, string } from 'yup'
import moment from 'moment'
import { matchIsValidTel } from '@center/ui-kit'

import { toast } from 'components/global/CUK/Toast/Toast'
import client from 'apollo-client'
import OPERATION_CREATE_COUPON from '../operations-gql/mutations'

export const DEFAULT_COUPON = {
  site_id: null,
  amount: 1,
  dates: { startDate: null, endDate: null },
  offer: null,
  with_offer: false,
  description: '',
  max_usage: 1,
  phone_number: '',
  send_sms: false,
  with_ran: false,
  offer_id: null,
}

export const buildSchema = (t) =>
  object({
    site_id: string().nullable().required(t('coupons.errors.create.sites')),
    amount: number()
      .min(1, t('coupons.errors.create.amount_min'))
      .max(10000, t('coupons.errors.create.amount_max')),
    dates: object().shape({
      startDate: date()
        .nullable(true)
        .typeError(t('coupons.errors.create.dates'))
        .test(
          'dates required',
          t('coupons.errors.create.dates'),
          (value, context) => {
            const formValues = context.from[context.from.length - 1].value
            const hasOffer = Boolean(formValues.offer_id)
            if (hasOffer) {
              return true
            } else {
              return Boolean(value)
            }
          },
        ),
      endDate: date()
        .nullable(true)
        .typeError(t('coupons.errors.create.dates'))
        .test(
          'dates required',
          t('coupons.errors.create.dates'),
          (value, context) => {
            const formValues = context.from[context.from.length - 1].value
            const hasOffer = Boolean(formValues.offer_id)
            if (hasOffer) {
              return true
            } else {
              return Boolean(value)
            }
          },
        ),
    }),
    offer_id: number()
      .nullable()
      .integer()
      .typeError(t('coupons.errors.create.offer_id'))
      .test(
        'offer_or_dates_required',
        t('coupons.errors.create.offer_or_dates_required'),
        (value, context) => {
          const formValues = context.from[context.from.length - 1].value
          const { startDate, endDate } = formValues.dates || {}
          const hasOffer = Boolean(value)
          const hasDates = Boolean(startDate && endDate)
          return hasOffer || hasDates
        }
      ),
    with_offer: bool(),
    description: string().required(t('coupons.errors.create.description')),
    max_usage: number(),
    phone_number: string()
      .typeError(t('coupons.errors.create.phone_number'))
      .test(
        'matchIsValidTel',
        t('coupons.errors.create.phone_number'),
        (value) => {
          if (value && value.trim().length > 0) {
            return matchIsValidTel(value.trim())
          } else {
            return true
          }
        }
      ),
    send_sms: bool().required(t('coupons.errors.create.send_sms')),
    with_ran: bool().required(t('coupons.errors.create.with_ran')),
  })

const useSave = () => {
  const { t } = useTranslation()

  const [create, { loading, error }] = useMutation(
    OPERATION_CREATE_COUPON,
    {
      onCompleted: (data) => {
        const coupons = data?.coupons
        
        if (!coupons?.length) {
          toast.success(t('coupons.create.toast_success'))
        } else if (coupons.length === 1) {
          const couponCode = coupons[0].code || coupons[0].id
          toast.success(
            <>
              {t('coupons.create.toast_success_base')} <span style={{fontWeight: '600'}}>{couponCode}</span> {t('coupons.create.toast_success_suffix')}
            </>
          )
        } else {
          toast.success(t('coupons.create.toast_success'))
        }
  
        setTimeout(() => {
          client.refetchQueries({
            include: ['findCoupons']
          })
        }, 2500)
      },
      onError: () => {
        toast.error(t('coupons.create.toast_error'))
      }
    }
  )

  const save = async (submitData) => {
    const {
      site_id,
      amount,
      dates,
      offer_id,
      description,
      max_usage,
      phone_number,
      send_sms,
      with_ran,
    } = submitData
    const { data } = await create({
      variables: {
        sf_id: site_id,
        amount,
        starts_at: dates.startDate ? moment(dates.startDate).startOf('day') : undefined,
        ends_at: dates.endDate ? moment(dates.endDate).endOf('day') : undefined,
        description,
        max_usage,
        phone_number,
        send_sms,
        with_ran_access: with_ran,
        offer_id: offer_id,
        type: 'Wifirstsite',
      },
    })
    if (data?.coupons) {
      return true
    }
  }

  return {
    loading,
    error,
    save
  }
}

export default useSave